import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import {
  convertLatexStart,
  convertLatexSuccess,
  convertLatexFailure,
  resetQuestions
} from '../store/slices/appSlice'
import { convertToLatex, translateQuestions, saveQuestionsToHistory } from '../services/api'

type Language = 'zh-CN' | 'zh-TW' | 'en'

const DIFFICULTY_LABELS = {
  'zh-CN': {
    easy: '简单',
    medium: '中等',
    hard: '困难'
  },
  'zh-TW': {
    easy: '簡單',
    medium: '中等',
    hard: '困難'
  },
  'en': {
    easy: 'Easy',
    medium: 'Medium',
    hard: 'Hard'
  }
}

const DIFFICULTY_COLORS = {
  easy: 'bg-green-100 text-green-800',
  medium: 'bg-yellow-100 text-yellow-800',
  hard: 'bg-red-100 text-red-800'
}

const LANGUAGE_OPTIONS = [
  { value: 'zh-CN' as Language, label: '简体中文' },
  { value: 'zh-TW' as Language, label: '繁體中文' },
  { value: 'en' as Language, label: 'English' }
]

export default function QuestionPage() {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const [copied, setCopied] = useState(false)
  const [selectedLanguage, setSelectedLanguage] = useState<Language>('zh-CN')
  const [translatedQuestions, setTranslatedQuestions] = useState<any>(null)
  const [isTranslating, setIsTranslating] = useState<Record<Language, boolean>>({
    'zh-CN': false,
    'zh-TW': false,
    'en': false
  })
  const [translateError, setTranslateError] = useState<string | null>(null)
  const [saveStatus, setSaveStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const [latexLanguage, setLatexLanguage] = useState<Language>('zh-CN')

  const {
    questions,
    selectedLiterature,
    apiKey,
    selectedModel,
    latexContent,
    isConvertingLatex,
    convertError
  } = useAppSelector((state) => state.app)

  // 当前显示的题目（原始或翻译后的）
  const displayQuestions = selectedLanguage === 'zh-CN'
    ? questions
    : (translatedQuestions?.[selectedLanguage] || questions)

  // 页面加载后自动在后台翻译繁体中文和英文
  useEffect(() => {
    if (questions.length > 0 && apiKey) {
      // 自动翻译繁体中文
      if (!translatedQuestions?.['zh-TW']) {
        handleTranslate('zh-TW', true)
      }
      // 自动翻译英文
      if (!translatedQuestions?.['en']) {
        handleTranslate('en', true)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions, apiKey])

  // 自动保存题目到后端数据库
  useEffect(() => {
    if (questions.length > 0) {
      console.log('📤 开始保存题目到数据库...', questions)
      saveQuestionsToHistory(questions)
        .then(result => {
          if (result.success) {
            console.log('✅ 题目已保存到历史记录:', result.message)
            setSaveStatus({ type: 'success', message: result.message })
            // 3秒后自动隐藏成功提示
            setTimeout(() => setSaveStatus(null), 3000)
          } else {
            console.error('⚠️ 保存题目失败:', result.message)
            setSaveStatus({ type: 'error', message: `保存失败: ${result.message}` })
          }
        })
        .catch(error => {
          console.error('⚠️ 保存题目出错:', error)
          const errorMsg = error instanceof Error ? error.message : '未知错误'
          setSaveStatus({ type: 'error', message: `保存出错: ${errorMsg}` })
        })
    }
  }, [questions])

  const handleTranslate = async (targetLanguage: Language, silent = false) => {
    if (targetLanguage === 'zh-CN' || !apiKey) {
      return
    }

    setIsTranslating(prev => ({ ...prev, [targetLanguage]: true }))
    if (!silent) {
      setTranslateError(null)
    }

    try {
      console.log('🔄 开始翻译到:', targetLanguage)
      console.log('📝 原始题目数据:', questions)

      const translated = await translateQuestions(
        questions,
        targetLanguage,
        apiKey,
        selectedModel
      )

      console.log('✅ 翻译完成，结果:', translated)
      console.log('📊 翻译后第一题的 literatureSummary:', translated[0]?.literatureSummary)

      setTranslatedQuestions((prev: any) => ({
        ...prev,
        [targetLanguage]: translated
      }))
    } catch (error) {
      if (!silent) {
        setTranslateError(error instanceof Error ? error.message : '翻译失败')
      } else {
        console.error(`Background translation to ${targetLanguage} failed:`, error)
      }
    } finally {
      setIsTranslating(prev => ({ ...prev, [targetLanguage]: false }))
    }
  }

  const handleConvertToLatex = async (language: Language) => {
    dispatch(convertLatexStart())
    setLatexLanguage(language)

    try {
      const questionsToConvert = language === 'zh-CN'
        ? questions
        : (translatedQuestions?.[language] || questions)

      const latex = await convertToLatex(questionsToConvert)
      dispatch(convertLatexSuccess(latex))
    } catch (error) {
      dispatch(convertLatexFailure(
        error instanceof Error ? error.message : 'LaTeX转换失败'
      ))
    }
  }

  const handleCopyLatex = () => {
    navigator.clipboard.writeText(latexContent)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleDownloadLatex = () => {
    const blob = new Blob([latexContent], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    const langLabel = LANGUAGE_OPTIONS.find(l => l.value === latexLanguage)?.label || 'zh-CN'
    a.download = `questions_${langLabel}.tex`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const handleBackToLiterature = () => {
    dispatch(resetQuestions())
    navigate('/literature')
  }

  if (questions.length === 0) {
    return (
      <div className="max-w-4xl mx-auto text-center space-y-6">
        <div className="card">
          <div className="py-12">
            <p className="text-gray-600 mb-4">暂无题目数据</p>
            <button
              onClick={() => navigate('/literature')}
              className="btn btn-primary"
            >
              返回文献列表
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* 标题和语言选择器 */}
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-gray-900">生成的题目</h1>
          {selectedLiterature && (
            <p className="text-gray-600 mt-2 line-clamp-1">
              基于: {selectedLiterature.title}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {/* 语言切换器 */}
          <div className="flex items-center gap-2 bg-white border-2 border-gray-200 rounded-lg p-1">
            {LANGUAGE_OPTIONS.map((lang) => (
              <button
                key={lang.value}
                onClick={() => setSelectedLanguage(lang.value)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
                  selectedLanguage === lang.value
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                {lang.label}
                {isTranslating[lang.value] && (
                  <div className="animate-spin rounded-full h-3 w-3 border-2 border-current border-t-transparent"></div>
                )}
              </button>
            ))}
          </div>
          <button
            onClick={handleBackToLiterature}
            className="btn btn-secondary whitespace-nowrap"
          >
            返回文献列表
          </button>
        </div>
      </div>

      {/* 数据库保存状态提示 */}
      {saveStatus && (
        <div className={`card ${
          saveStatus.type === 'success'
            ? 'bg-green-50 border-green-200'
            : 'bg-red-50 border-red-200'
        }`}>
          <div className="flex items-center gap-3">
            <span className="text-lg">
              {saveStatus.type === 'success' ? '✅' : '❌'}
            </span>
            <div className="flex-1">
              <p className={`text-sm font-medium ${
                saveStatus.type === 'success' ? 'text-green-900' : 'text-red-900'
              }`}>
                {saveStatus.message}
              </p>
              {saveStatus.type === 'error' && (
                <p className="text-xs text-red-700 mt-1">
                  ⚠️ 数据库可能未初始化，请参考 backend/DATABASE_SETUP.md 进行配置
                </p>
              )}
            </div>
            {saveStatus.type === 'error' && (
              <button
                onClick={() => setSaveStatus(null)}
                className="text-red-600 hover:text-red-800"
              >
                ✕
              </button>
            )}
          </div>
        </div>
      )}

      {/* 翻译状态提示 - 只在用户主动切换到未翻译的语言时显示 */}
      {selectedLanguage !== 'zh-CN' && isTranslating[selectedLanguage] && !translatedQuestions?.[selectedLanguage] && (
        <div className="card bg-blue-50 border-blue-200">
          <div className="flex items-center gap-3">
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary-600 border-t-transparent"></div>
            <p className="text-sm text-blue-800">正在翻译题目...</p>
          </div>
        </div>
      )}

      {/* 翻译错误提示 */}
      {translateError && (
        <div className="card bg-red-50 border-red-200">
          <p className="text-sm text-red-800">{translateError}</p>
          <button
            onClick={() => handleTranslate(selectedLanguage)}
            className="btn btn-secondary mt-3"
          >
            重试
          </button>
        </div>
      )}

      {/* 题目列表 */}
      <div className="space-y-6">
        {displayQuestions.map((question: any, index: number) => (
          <div key={index} className="card">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="flex-1">
                <h2 className="text-xl font-semibold text-gray-900">
                  {selectedLanguage === 'en' ? `Question ${index + 1}` :
                   selectedLanguage === 'zh-TW' ? `第 ${index + 1} 題` :
                   `第 ${index + 1} 题`}
                </h2>
                {question.id && (
                  <p className="text-xs text-gray-500 mt-1">
                    ID: {question.id}
                  </p>
                )}
              </div>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                DIFFICULTY_COLORS[(question.difficulty as 'easy' | 'medium' | 'hard') || 'medium']
              }`}>
                {DIFFICULTY_LABELS[selectedLanguage][(question.difficulty as 'easy' | 'medium' | 'hard') || 'medium']}
              </span>
            </div>

            {/* 文献简介 */}
            {question.literatureSummary && (
              <div className="mb-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <h3 className="text-sm font-medium text-amber-900 mb-1">
                  {selectedLanguage === 'en' ? 'Literature Summary' :
                   selectedLanguage === 'zh-TW' ? '文獻摘要' :
                   '文献摘要'}
                </h3>
                <p className="text-sm text-gray-700 leading-relaxed">{question.literatureSummary}</p>
              </div>
            )}

            {/* 题目 */}
            <div className="mb-4">
              <p className="text-gray-900 leading-relaxed">{question.question}</p>
            </div>

            {/* 选项 */}
            <div className="space-y-2 mb-4">
              {question.options.map((option: string, optIndex: number) => (
                <div
                  key={optIndex}
                  className={`p-3 rounded-lg ${
                    option.startsWith(question.answer + '.')
                      ? 'bg-green-50 border-2 border-green-300'
                      : 'bg-gray-50 border border-gray-200'
                  }`}
                >
                  <p className="text-gray-900">{option}</p>
                </div>
              ))}
            </div>

            {/* 答案 */}
            <div className="mb-4 p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm font-medium text-green-900">
                {selectedLanguage === 'en' ? `Correct Answer: ${question.answer}` :
                 selectedLanguage === 'zh-TW' ? `正確答案: ${question.answer}` :
                 `正确答案: ${question.answer}`}
              </p>
            </div>

            {/* 解析 */}
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h3 className="font-medium text-gray-900 mb-2">
                {selectedLanguage === 'en' ? 'Analysis' :
                 selectedLanguage === 'zh-TW' ? '解析' :
                 '解析'}
              </h3>
              <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                {question.analysis}
              </p>

              {/* 知识点 */}
              {question.knowledgePoints && question.knowledgePoints.length > 0 && (
                <div className="mt-3 pt-3 border-t border-blue-200">
                  <h4 className="text-xs font-medium text-gray-700 mb-2">
                    {selectedLanguage === 'en' ? 'Key Concepts' :
                     selectedLanguage === 'zh-TW' ? '核心知識點' :
                     '核心知识点'}:
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {question.knowledgePoints.map((kp: string, kpIndex: number) => (
                      <span key={kpIndex} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                        {kp}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* 文献链接 */}
              {question.literatureUrl && (
                <div className="mt-3 pt-3 border-t border-blue-200">
                  <a
                    href={question.literatureUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:text-blue-700 underline"
                  >
                    {selectedLanguage === 'en' ? '📄 View source literature' :
                     selectedLanguage === 'zh-TW' ? '📄 查看原文獻' :
                     '📄 查看原文献'}
                  </a>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* LaTeX 导出 */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">LaTeX 导出</h2>
          <div className="flex items-center gap-3">
            <select
              value={latexLanguage}
              onChange={(e) => setLatexLanguage(e.target.value as Language)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {LANGUAGE_OPTIONS.map((lang) => (
                <option key={lang.value} value={lang.value}>
                  {lang.label}
                </option>
              ))}
            </select>
            <button
              onClick={() => handleConvertToLatex(latexLanguage)}
              disabled={isConvertingLatex}
              className="btn btn-primary"
            >
              {isConvertingLatex ? '转换中...' : '转换为 LaTeX'}
            </button>
          </div>
        </div>

        {convertError && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800">{convertError}</p>
          </div>
        )}

        {latexContent && (
          <div className="space-y-3">
            <div className="relative">
              <pre className="p-4 bg-gray-900 text-gray-100 rounded-lg overflow-x-auto text-sm font-mono max-h-96">
                {latexContent}
              </pre>
              <div className="absolute top-2 right-2 flex items-center gap-2">
                <button
                  onClick={handleDownloadLatex}
                  className="px-3 py-1 bg-primary-600 text-white rounded text-sm font-medium hover:bg-primary-700 transition-colors"
                >
                  下载
                </button>
                <button
                  onClick={handleCopyLatex}
                  className="px-3 py-1 bg-white text-gray-900 rounded text-sm font-medium hover:bg-gray-100 transition-colors"
                >
                  {copied ? '已复制!' : '复制'}
                </button>
              </div>
            </div>
            <p className="text-sm text-gray-600">
              当前语言: <strong>{LANGUAGE_OPTIONS.find(l => l.value === latexLanguage)?.label}</strong> |
              复制或下载后粘贴到 LaTeX 编辑器中即可生成 PDF 文档
            </p>
          </div>
        )}
      </div>

      {/* 操作提示 */}
      <div className="card bg-blue-50 border-2 border-blue-200">
        <h3 className="font-semibold text-gray-900 mb-2">使用说明</h3>
        <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
          <li>使用顶部的语言切换器可以查看简体中文、繁体中文、英文版本的题目</li>
          <li>选择导出语言，点击"转换为 LaTeX"生成对应语言的LaTeX代码</li>
          <li>点击"下载"按钮保存LaTeX文件，或点击"复制"按钮复制到剪贴板</li>
          <li>将LaTeX代码粘贴到 Overleaf 或本地LaTeX编辑器编译生成PDF</li>
          <li>如需生成更多题目,可返回文献列表选择其他文献</li>
        </ul>
      </div>
    </div>
  )
}
